import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageCard from '../../../components/page-card';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate } from '../../../i18n';
import { GET_USER_COUPONS_QUERY_KEY, UserCouponDto, useGetUserCoupons } from '../hooks/useGetUserCoupons';
import { CouponDto } from '../hooks/useListCoupons';
import AssignCouponToUserModal from './AssignCouponToUserModal';
import { useApiClient } from '../../../apiClient';
import { isUnreadable } from '../../analytics/utils/statValue';

interface UserCouponListProps {
  userId: string;
}

export default function UserCouponList({ userId }: UserCouponListProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  // `GET coupons/admin/user/:userId` carries the same AdminAuthGuard as the
  // list read, so it can be refused the same way. Without `isError` a refusal
  // rendered "no coupons assigned to this user" — next to the Assign button,
  // which is exactly the wrong thing to believe. See
  // `coupon-read-error-gate.test.ts`.
  const { data: userCoupons, isLoading, isError } = useGetUserCoupons(userId);
  const [revokingCouponId, setRevokingCouponId] = useState<string | null>(null);
  const {
    isOpen: isAssignOpen,
    onOpen: onAssignOpen,
    onClose: onAssignClose,
  } = useDisclosure();

  const handleRevoke = async (couponId: string, couponName: string) => {
    if (window.confirm(`${translate('coupon.confirm_revoke_user')} "${couponName}"?`)) {
      setRevokingCouponId(couponId);
      try {
        await apiClient.fetchApi({
          endpoint: `coupons/admin/${couponId}/assign/${userId}`,
          method: 'DELETE',
        });
        
        // Invalidate the user coupons query
        queryClient.invalidateQueries({
          queryKey: GET_USER_COUPONS_QUERY_KEY(userId),
        });
        
        toast({
          title: translate('coupon.user_revoked_successfully'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: translate('coupon.revoke_failed'),
          description: error instanceof Error ? error.message : undefined,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setRevokingCouponId(null);
      }
    }
  };

  const getDiscountBadge = (coupon: CouponDto) => {
    if (coupon.discountType === 'PERCENTAGE') {
      return (
        <Badge colorScheme="purple" fontSize="sm">
          -{coupon.discountValue}%
        </Badge>
      );
    }
    return (
      <Badge colorScheme="brand" fontSize="sm">
        -{coupon.discountValue} {(coupon.currency || '').toUpperCase()}
      </Badge>
    );
  };

  const getStatusBadge = (userCoupon: UserCouponDto) => {
    const isExpired = new Date(userCoupon.coupon.expiresAt) < new Date();
    const isFullyUsed = userCoupon.usageCount >= userCoupon.coupon.maxUsagePerUser;

    if (isExpired) {
      return <Badge colorScheme="red">{translate('coupon.expired')}</Badge>;
    }
    if (isFullyUsed) {
      return <Badge colorScheme="orange">{translate('coupon.fully_used')}</Badge>;
    }
    return <Badge colorScheme="green">{translate('userMembership.active')}</Badge>;
  };

  return (
    <PageCard
      title={translate('coupon.coupons')}
      rightHeader={
        <Button
          leftIcon={<AddIcon />}
          size="sm"
          colorScheme="brand"
          onClick={onAssignOpen}
        >
          {translate('coupon.assign_coupon')}
        </Button>
      }
    >
      <Box p={4} px={6}>
        {/* `isUnreadable`, not the raw flag: React Query KEEPS `userCoupons` through
            a failed background refetch, and this arm REPLACES the control. Reading
            the flag alone took a working assigned-coupon list and its revoke buttons
            away from the operator because a refresh was refused
            (useGetUserCoupons, staleTime 0 — R132-1). */}
        {isUnreadable(isError, userCoupons) ? (
          <InlineErrorState message={translate('common.unexpected_error')} />
        ) : isLoading ? (
          <Text color="gray.500">{translate('coupon.loading')}</Text>
        ) : !userCoupons?.length ? (
          <Text color="gray.500">{translate('coupon.no_assigned_users')}</Text>
        ) : (
          <VStack align="stretch" spacing={3}>
            {userCoupons.map((userCoupon) => (
              <Flex
                key={userCoupon.id}
                justify="space-between"
                align="center"
                p={3}
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
                _hover={{ bg: 'gray.50', borderColor: 'brand.300' }}
                cursor="pointer"
                onClick={() => navigate(`/admin/coupons/${userCoupon.coupon.id}`)}
              >
                <Box flex={1} minW={0}>
                  <HStack spacing={2} mb={1}>
                    <Text fontWeight="bold" fontSize="md">
                      {userCoupon.coupon.name}
                    </Text>
                    {getDiscountBadge(userCoupon.coupon)}
                    {getStatusBadge(userCoupon)}
                  </HStack>
                  <HStack spacing={4} fontSize="sm" color="gray.600">
                    <Text>
                      {translate('coupon.usage')}: {userCoupon.usageCount} /{' '}
                      {userCoupon.coupon.maxUsagePerUser}
                    </Text>
                    <Text>
                      {translate('coupon.assigned_at')}:{' '}
                      {new Date(userCoupon.assignedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Text>
                    <Text>
                      {translate('coupon.expires_at')}:{' '}
                      {new Date(userCoupon.coupon.expiresAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Text>
                  </HStack>
                </Box>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  leftIcon={<DeleteIcon />}
                  flexShrink={0}
                  isLoading={revokingCouponId === userCoupon.coupon.id}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRevoke(
                      userCoupon.coupon.id,
                      userCoupon.coupon.name,
                    );
                  }}
                >
                  {translate('coupon.revoke')}
                </Button>
              </Flex>
            ))}
          </VStack>
        )}
      </Box>

      <AssignCouponToUserModal
        userId={userId}
        isOpen={isAssignOpen}
        onClose={onAssignClose}
      />
    </PageCard>
  );
}
